import { ArrowDown } from "lucide-react";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { WORLDS, PIPELINE, FUTURE_CATEGORIES, EXPEDITIONS_FAQ, EXPEDITION_STATUS } from "@/lib/expeditions";

export const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />{children}
  </p>
);

export function FiveWorldsSection({ counts, settings, pickTerrain }) {
  return (
    <section data-testid="five-worlds" className="bg-ptf-primary text-ptf-bg py-20 md:py-28 grain">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow light>Expeditions by terrain · Where do you want to go?</Eyebrow></Reveal>
        <MaskText lines={["The world is", "the field."]} className="font-display font-extrabold uppercase text-4xl sm:text-6xl leading-[0.92] tracking-tight" />
        <Reveal delay={0.1}>
          <p className="mt-5 max-w-2xl text-sm text-ptf-bg/70 leading-relaxed">
            Curated journeys across mountains, rivers, oceans, landscapes and wildlife
            territories, in India and around the world.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-ptf-bg/10 border border-ptf-bg/10">
          {WORLDS.map((w, i) => {
            const has = (counts[w.key] || 0) > 0;
            return (
              <Reveal key={w.key} delay={i * 0.05} className="h-full">
                <button type="button" data-testid={`world-${w.key.toLowerCase()}`} onClick={() => pickTerrain(w.key, has)} className="group text-left bg-ptf-primary h-full w-full flex flex-col hover:bg-ptf-secondary transition-colors duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={settings?.terrain_images?.[w.key] || w.image} alt={`${w.name} expeditions`} className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-[1.04] transition-all duration-700" loading="lazy" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <span className="font-display text-xl text-ptf-bg/30">{w.num}</span>
                    <h3 className="font-display uppercase text-2xl tracking-tight">{w.name}</h3>
                    <p className="mt-2 text-xs text-ptf-bg/65 leading-relaxed flex-1">{w.blurb}</p>
                    {has ? (
                      <span className="mt-3 text-[0.6rem] uppercase tracking-[0.16em] text-ptf-accent">{counts[w.key]} expedition{counts[w.key] === 1 ? "" : "s"} · Explore</span>
                    ) : (
                      <span className="mt-3 text-[0.6rem] uppercase tracking-[0.16em] text-ptf-bg/55">Expeditions in development · See what's coming</span>
                    )}
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PropositionSection() {
  return (
    <section className="bg-ptf-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>The proposition</Eyebrow></Reveal>
          <MaskText lines={["We don't just", "travel."]} className="font-display font-extrabold uppercase text-4xl sm:text-6xl leading-[0.92] tracking-tight text-ptf-primary" />
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <p className="text-lg text-ptf-text/80 leading-relaxed">
              We create journeys with a purpose. We enter the field to experience
              places, understand landscapes, meet people, observe wildlife and
              document what we find. We go there to experience it, document it, and
              tell the story.
            </p>
            <p className="mt-4 text-sm text-ptf-text/65 leading-relaxed">
              A journey may become a film, a photography project, a field story, a
              journal or educational material. Each expedition states the outputs it
              is actually producing; nothing is claimed by default.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function FilmPipelineSection() {
  return (
    <section data-testid="film-pipeline" className="bg-white border-y border-ptf-primary/10 py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>The film pipeline</Eyebrow></Reveal>
        <MaskText lines={["From the field.", "To the screen."]} className="font-display font-extrabold uppercase text-4xl sm:text-6xl leading-[0.92] tracking-tight text-ptf-primary" />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-5">
          {PIPELINE.map((s, i) => (
            <Reveal key={s} delay={i * 0.05}>
              <div className="border-t-2 border-ptf-primary/15 pt-4 h-full">
                <span className="font-display text-2xl text-ptf-primary/25">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-1 font-display uppercase text-lg tracking-tight text-ptf-primary leading-tight">{s}</p>
                {i < PIPELINE.length - 1 && <ArrowDown className="lg:hidden h-4 w-4 text-ptf-primary/25 mt-3" />}
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-xs text-ptf-text/55 leading-relaxed max-w-2xl">
          Films may be submitted to appropriate film festivals in India and
          internationally, subject to editorial completion, eligibility and festival
          selection. No selection or award is claimed unless verified.
        </p>
        <div className="mt-8"><PtfButton to="/films" data-testid="pipeline-films-cta">Visit the Films Archive</PtfButton></div>
      </div>
    </section>
  );
}

export function InDevelopmentSection() {
  return (
    <section id="in-development" data-testid="in-development" className="bg-ptf-bg py-16 md:py-24 scroll-mt-24">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>Expeditions in development</Eyebrow></Reveal>
        <p className="text-ptf-text/70 leading-relaxed max-w-2xl">
          Explore journeys currently being researched in this field. These are the
          directions Planet Trotter is developing next: categories under research,
          not confirmed expeditions; applications open only when a journey reaches Upcoming.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {FUTURE_CATEGORIES.map((c) => (
            <span key={c} className="text-[0.65rem] uppercase tracking-[0.12em] border border-dashed border-ptf-primary/30 text-ptf-text/60 px-3.5 py-2">{c} · {EXPEDITION_STATUS.CONCEPT.label}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ExpeditionsFAQSection() {
  return (
    <section data-testid="expeditions-faq" className="bg-white border-t border-ptf-primary/10 py-20 md:py-28">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>Common questions</Eyebrow></Reveal>
        <div className="space-y-0">
          {EXPEDITIONS_FAQ.map((f) => (
            <div key={f.q} className="py-6 border-t border-ptf-primary/12 grid grid-cols-1 md:grid-cols-12 gap-3">
              <h3 className="md:col-span-5 font-display uppercase text-xl tracking-tight text-ptf-primary">{f.q}</h3>
              <p className="md:col-span-7 text-sm text-ptf-text/70 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
